"use client"
import { forwardRef, useEffect, useMemo, useState } from "react";
import Button from "src/components/button/Button";
import { useMovieActivityContext } from "src/components/film/filmwebid/MovieActivityContext";
import LoadingIndicator from "src/components/loadingindicator/LoadingIndicator";
import SpeechBubbleSvg from "src/components/svg/SpeechBubbleSvg";
import { useUserContext } from "src/lib/contexts/UserContext";
import IMovieReview from "src/lib/filmwebid/IMovieReview";
import useFilmReviewList from "src/lib/filmwebid/useFilmReviewList";
import classNames from 'classnames';
import styles from './UserReviews.module.css';

import dynamic from "next/dynamic";

const ReviewPanel = dynamic(() => import('src/components/filmwebid/ReviewPanel'), {
  loading: () => <p>Loading...</p>,
  ssr: false
});

const ReviewList = dynamic(() => import('src/components/film/filmwebid/ReviewList'), {
  loading: () => <p>Loading...</p>,
  ssr: false
});

//#region [Props]
type UserReviewsProps = {
  edi?: string;
  streamingId?: number;
  title: string;
};
//#endregion

//#region [Component]
const UserReviews = forwardRef<HTMLElement, UserReviewsProps>(({ edi, streamingId, title }, ref) => {
  const movieActivityContext = useMovieActivityContext();
  const userContext = useUserContext();
  const [_reviewList, totalReviews, isFetchingList, fetchNext] = useFilmReviewList(edi, streamingId);
  const [showAll, setShowAll] = useState(false);
  const [showReviewPanel, setShowReviewPanel] = useState(false);

  const reviewList: IMovieReview[] | undefined = useMemo(() => {
    if (!isFetchingList) {
      if (movieActivityContext.activity?.movieUserInteraction?.review) {
        const serverReview = _reviewList?.find(sr => sr.reviewId === movieActivityContext.activity?.movieUserInteraction?.reviewId);
        if (serverReview) {
          if (serverReview.reviewText !== movieActivityContext.activity.movieUserInteraction.review) {
            return [{ ...serverReview, reviewText: movieActivityContext.activity.movieUserInteraction.review }, ..._reviewList.filter(r => r.reviewId !== serverReview.reviewId)];
          }
        } else {
          let mr: IMovieReview = {
            reviewId: movieActivityContext.activity.movieUserInteraction.reviewId!,
            reviewText: movieActivityContext.activity.movieUserInteraction.review,
            rating: movieActivityContext.activity.movieUserInteraction.rating,
            userName: userContext.userProfile?.userName!,
            hasUploadedPicture: !!userContext.userProfile?.picture,
            pictureUrl: userContext.userProfile?.picture,
            userId: userContext.userProfile?.userId!
          };
          return [mr, ...(_reviewList ?? [])];
        }
      } else {
        const userId = userContext.getUserId();
        return _reviewList?.filter(sr => sr.userId !== userId) ?? [];
      }
    }
    return _reviewList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_reviewList, isFetchingList, movieActivityContext.activity?.movieUserInteraction?.review, movieActivityContext.activity?.movieUserInteraction?.reviewId, movieActivityContext.activity?.movieUserInteraction?.rating]);

  useEffect(() => {
    setShowAll(false);
  }, [edi, streamingId]);

  const _showReviewPanel = () => {
    if (userContext.isAuthenticated) {
      setShowReviewPanel(true);
    } else {
      userContext.doLogin();
    }
  };

  const onReviewPanelClose = (doUpdate: boolean) => {
    setShowReviewPanel(false);
    if (doUpdate) {
      movieActivityContext.activityUpdater();
    }
  }

  return <section className={styles.SUserReviewsContainer} ref={ref}>
    <h4 className={styles.SHeading}>Anmeldelser</h4>

    <div className={styles.SReviews}>
      <ReviewList reviewList={reviewList} edi={edi} streamingId={streamingId} setShowReviewPanel={setShowReviewPanel} showAll={showAll} />
      {(reviewList?.length ?? 0) === 0 && <div className={styles.SNoReviews}>Foreløpig har ingen anmeldt {title}</div>}
      {(isFetchingList || movieActivityContext.isLoading) && <LoadingIndicator position="InlineCenter" />}
    </div>

    {!isFetchingList && <div className={styles.SActions}>
      {(reviewList?.length ?? 0) > 2 && !showAll && <Button
        className={classNames(styles.SSeeMoreButton, {
          [styles['show-mobile']]: (reviewList?.length ?? 0) > 2,
          [styles['show-desktop']]: (reviewList?.length ?? 0) >= 6
        })}
        disabled={movieActivityContext.isLoading}
        text="Se flere"
        onClick={() => { setShowAll(true); fetchNext(totalReviews); }} />}
    </div>}
    <Button
      className={styles.SWriteReviewButton}
      disabled={movieActivityContext.isLoading}
      onClick={_showReviewPanel}
      text="Skriv anmeldelse"
      icon={<SpeechBubbleSvg selected={!!movieActivityContext.activity?.movieUserInteraction?.review} />}
    />
    {showReviewPanel && <ReviewPanel
      onClose={onReviewPanelClose}
      streamingId={streamingId}
      edi={edi}
      reviewId={movieActivityContext?.activity?.movieUserInteraction?.reviewId}
      userRating={movieActivityContext?.activity?.movieUserInteraction?.rating}
      userReview={movieActivityContext?.activity?.movieUserInteraction?.review}
    />}
  </section>;

});

UserReviews.displayName = "UserReviews";

export default UserReviews;
//#endregion
